import React, { Fragment } from "react";
import {
  TextField,
  Stack,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { INFORMATION_FIELD_REQUEST } from "../../../../../../enums/ApiPaths";
import { useForm, Controller } from "react-hook-form";
import Wrapper from "../../../../../../components/wrappers/Wrapper";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { informationFieldFormResolver } from "../utils/InformationFieldFormResolver";
import { informationFieldCategories } from "../enums/InformationFieldCategories";
import { INFO_FIELD, ABOUT_US } from "../../../../../../enums/QueryKeys";
import usePut from "../../../../../../hooks/usePut";
import { informationFieldMaxLengthTitle, informationFieldMaxLengthValue } from "./InformationFieldForm";

export default function InformationFieldEditingForm({ informationField }) {
  const { id: clinicId } = useParams();
  const { t } = useTranslation();
  const editPath = INFORMATION_FIELD_REQUEST + clinicId;

  const put = usePut(editPath, INFO_FIELD, [ABOUT_US]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      fieldValue: informationField?.fieldValue,
      label: informationField?.label,
      category: informationField?.category,
    },
    resolver: zodResolver(informationFieldFormResolver(t)),
  });

  const editSubmit = async (data) => {
    const obj = {
      id: informationField?.id,
      label: data.label,
      fieldValue: data?.fieldValue,
      category: data?.category,
    };

    put.submitCallback(obj);
  };
  return (
    <Fragment>
      <form onSubmit={handleSubmit(editSubmit)}>
        <Wrapper spacing={2} direction="column">
          <Controller
            name="label"
            control={control}
            rules={{ required: t("CantBeEmpty") }}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("Label")}
                fullWidth
                inputProps={{
                  maxLength: informationFieldMaxLengthTitle,
                  "data-testid": `clinic.info-fields.item.${informationField?.id}.edit-form.label-input`,
                }}
                error={!!errors.label}
                helperText={errors.label ? errors.label.message : ""}
              />
            )}
          />
          <Controller
            name="fieldValue"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("fieldValue")}
                fullWidth
                inputProps={{
                  maxLength: informationFieldMaxLengthValue,
                  "data-testid": `clinic.info-fields.item.${informationField?.id}.edit-form.value-input`,
                }}
                error={!!errors.fieldValue}
                helperText={errors.fieldValue ? errors.fieldValue.message : ""}
              />
            )}
          />
          <Controller
            name="category"
            defaultValue={""}
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors?.category}>
                <InputLabel>{t("Category")}</InputLabel>
                <Select
                  {...field}
                  label={t("Category")}
                  SelectDisplayProps={{
                    "data-testid": `clinic.info-fields.item.${informationField?.id}.edit-form.category-select`,
                  }}
                >
                  {informationFieldCategories.map((category) => (
                    <MenuItem
                      value={category?.id}
                      key={category?.id}
                      data-testid={`clinic.info-fields.item.${informationField?.id}.edit-form.category-select.option.${category?.id}`}
                    >
                      {t(category?.value)}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors?.category?.message || ""}
                </FormHelperText>
              </FormControl>
            )}
          />
        </Wrapper>
        <Stack direction={"row"} alignItems={"center"} spacing={2} mt={2}>
          <Button
            variant="contained"
            color="info"
            sx={{ mt: 2 }}
            type="submit"
            data-testid={`clinic.info-fields.item.${informationField?.id}.edit-form.save-button`}
          >
            {t("Save")}
          </Button>
        </Stack>
      </form>
    </Fragment>
  );
}
